// rrtvExchanges.js — TV schedule, global exchange table, per-exchange data
// builder, and the freezable rotation state machine.

import { useState, useEffect, useRef, useCallback } from 'react';
import { baseSymbol, nowBRT, brtMinutes } from './rrtvFormat';

// ---------- Schedule ----------
export const ALL_SLOTS = [
  { start: 20*60,    end: 22*60,    show: 'Ásia & Oceania',      badge: '🌏', days: [1,2,3,4,5] },
  { start:  5*60,    end:  7*60,    show: 'Abertura Europeia',   badge: '🌅', days: [1,2,3,4,5] },
  { start:  7*60,    end:  9*60,    show: 'Good Morning',        badge: '☀️', days: [1,2,3,4,5] },
  { start:  9*60,    end: 10*60,    show: 'Abertura B3 Futuros', badge: '📈', days: [1,2,3,4,5] },
  { start: 10*60,    end: 10*60+30, show: 'Abertura B3 À Vista', badge: '🔔', days: [1,2,3,4,5] },
  { start: 10*60+30, end: 13*60+30, show: 'Wall Street + PTAX',  badge: '🌎', days: [1,2,3,4,5] },
  { start: 13*60+30, end: 15*60+50, show: 'Fechamento Europeu',  badge: '🇪🇺', days: [1,2,3,4,5] },
  { start: 15*60+50, end: 17*60,    show: 'Ajuste do Dólar',     badge: '💵', days: [1,2,3,4,5] },
  { start: 17*60,    end: 18*60+30, show: 'Fechamento B3',       badge: '🏁', days: [1,2,3,4,5] },
  { start: 18*60+30, end: 20*60,    show: 'Prime Time',          badge: '🌟', days: [1,2,3,4,5] },
  { start:  9*60,    end: 13*60,    show: 'Documentário',        badge: '📺', days: [0,6] },
  { start: 13*60,    end: 17*60,    show: 'Crypto Pulse',        badge: '₿',  days: [0,6] },
  { start: 17*60,    end: 20*60,    show: 'Recapitulação',       badge: '📊', days: [0,6] },
  { start: 23*60,    end: 24*60,    show: 'The Week Ahead',      badge: '🔮', days: [0] },
];

export const fmtSlotTime = (mins) =>
  `${String(Math.floor(mins/60)).padStart(2,'0')}:${String(mins%60).padStart(2,'0')}`;

export const getTodaySlots = () => {
  const now = nowBRT(); const dow = now.getDay(); const cur = brtMinutes();
  const today = ALL_SLOTS.filter(s => s.days.includes(dow)).map(s => {
    const status = cur >= s.start && cur < s.end ? 'live'
                 : cur < s.start               ? 'upcoming' : 'done';
    return { ...s, status };
  });
  const live     = today.filter(s => s.status === 'live');
  const upcoming = today.filter(s => s.status === 'upcoming').sort((a,b) => a.start - b.start);
  const done     = today.filter(s => s.status === 'done').sort((a,b) => b.start - a.start);
  if (upcoming.length) upcoming[0] = { ...upcoming[0], status: 'next' };
  return [...live, ...upcoming, ...done].slice(0, 6);
};

// ---------- Exchanges ----------
// `suffixes` = Yahoo ticker suffix(es) used to bucket the full universe.
// NYSE/NASDAQ = '' (US tickers have no dot). B3 is fed from the live B3 feed
// (has volume + weekly), the rest from the worldwide universe by suffix.
export const EXCHANGES = [
  { id: 'B3',      label: 'B3',          open: 10*60,    close: 17*60+55, flag: '🇧🇷', tz: 'America/Sao_Paulo', suffixes: ['SA'] },
  { id: 'NYSE',    label: 'NYSE/NASDAQ', open: 10*60+30, close: 17*60,    flag: '🇺🇸', tz: 'America/New_York',  suffixes: [''] },
  { id: 'LSE',     label: 'Londres',     open:  6*60,    close: 14*60+30, flag: '🇬🇧', tz: 'Europe/London',     suffixes: ['L'] },
  { id: 'XETRA',  label: 'Frankfurt',   open:  4*60,    close: 12*60+30, flag: '🇩🇪', tz: 'Europe/Berlin',     suffixes: ['DE','F'] },
  { id: 'EURONX', label: 'Euronext',    open:  4*60,    close: 12*60+30, flag: '🇪🇺', tz: 'Europe/Paris',      suffixes: ['PA','AS','LS'] },
  { id: 'TYO',    label: 'Tóquio',      open: 22*60,    close:  6*60,    flag: '🇯🇵', tz: 'Asia/Tokyo',        suffixes: ['T'] },
  { id: 'HKG',    label: 'Hong Kong',   open: 22*60,    close:  5*60,    flag: '🇭🇰', tz: 'Asia/Hong_Kong',    suffixes: ['HK'] },
  { id: 'ASX',    label: 'Sydney',      open: 20*60,    close:  2*60,    flag: '🇦🇺', tz: 'Australia/Sydney',  suffixes: ['AX','NZ'] },
  { id: 'NSE',    label: 'Mumbai',      open:  0*60+30, close:  7*60,    flag: '🇮🇳', tz: 'Asia/Kolkata',      suffixes: ['NS','BO'] },
  { id: 'KOREA',  label: 'Seul',        open: 22*60,    close:  6*60,    flag: '🇰🇷', tz: 'Asia/Seoul',        suffixes: ['KS','KQ'] },
  { id: 'TAIWAN', label: 'Taipei',      open: 22*60,    close:  5*60,    flag: '🇹🇼', tz: 'Asia/Taipei',       suffixes: ['TW','TWO'] },
  { id: 'IDX',    label: 'Jacarta',     open: 22*60+30, close:  5*60,    flag: '🇮🇩', tz: 'Asia/Jakarta',      suffixes: ['JK'] },
  { id: 'TSX',    label: 'Toronto',     open: 11*60,    close: 17*60,    flag: '🇨🇦', tz: 'America/Toronto',   suffixes: ['TO','V'] },
  { id: 'OSLO',   label: 'Oslo',        open:  5*60,    close: 13*60+20, flag: '🇳🇴', tz: 'Europe/Oslo',       suffixes: ['OL'] },
  { id: 'HELSINKI',label:'Helsinki',    open:  5*60,    close: 13*60+30, flag: '🇫🇮', tz: 'Europe/Helsinki',   suffixes: ['HE'] },
  { id: 'TASE',   label: 'Tel Aviv',    open:  5*60+30, close: 13*60+25, flag: '🇮🇱', tz: 'Asia/Jerusalem',    suffixes: ['TA'] },
  { id: 'SGX',    label: 'Singapura',   open: 22*60+30, close:  5*60,    flag: '🇸🇬', tz: 'Asia/Singapore',    suffixes: ['SI'] },
  { id: 'TADAWUL',label: 'Riad',        open:  7*60,    close: 11*60,    flag: '🇸🇦', tz: 'Asia/Riyadh',       suffixes: ['SR'] },
];

export const isExchangeOpen = (exch, curMins) => {
  if (exch.open < exch.close) return curMins >= exch.open && curMins < exch.close;
  return curMins >= exch.open || curMins < exch.close;
};

const getSuffix = (tk) => { const p = (tk || '').split('.'); return p.length < 2 ? '' : p[p.length - 1]; };

// Bellwether anchors per exchange (full tickers) — the "emphasis" trio prefers
// these when present in the universe, then fills with the day's movers. B3 uses
// volume instead. Edit freely; unknown/absent names are simply skipped.
const ANCHORS = {
  NYSE:     ['AAPL','MSFT','NVDA','AMZN','GOOGL','META','TSLA'],
  LSE:      ['SHEL.L','AZN.L','HSBA.L','ULVR.L','BP.L'],
  XETRA:    ['SAP.DE','SIE.DE','ALV.DE','VOW3.DE','MBG.DE'],
  EURONX:   ['MC.PA','OR.PA','TTE.PA','ASML.AS','SU.PA'],
  TYO:      ['7203.T','6758.T','9984.T','8306.T','6861.T'],
  HKG:      ['0700.HK','9988.HK','0939.HK','1299.HK','0941.HK'],
  ASX:      ['BHP.AX','CBA.AX','CSL.AX','NAB.AX','WBC.AX'],
  NSE:      ['RELIANCE.NS','TCS.NS','HDFCBANK.NS','INFY.NS','ICICIBANK.NS'],
  KOREA:    ['005930.KS','000660.KS','005380.KS','051910.KS'],
  TAIWAN:   ['2330.TW','2317.TW','2454.TW','2308.TW'],
  IDX:      ['BBCA.JK','BBRI.JK','BMRI.JK','TLKM.JK'],
  TSX:      ['RY.TO','TD.TO','SHOP.TO','ENB.TO','CNR.TO'],
  OSLO:     ['EQNR.OL','DNB.OL','TEL.OL'],
  HELSINKI: ['NOKIA.HE','SAMPO.HE','NESTE.HE'],
  TASE:     ['TEVA.TA','LUMI.TA','POLI.TA'],
  SGX:      ['D05.SI','O39.SI','U11.SI','Z74.SI'],
  TADAWUL:  ['2222.SR','1120.SR','2010.SR','1180.SR'],
};

// Build per-exchange ticker arrays. B3 from the live feed; everyone else from
// the full worldwide universe bucketed by ticker suffix (so NYSE/Riad/etc.
// populate with their real gainers/losers, not just the global top-movers).
export const buildExchangeMap = (b3Gainers, b3Losers, b3Active, universe, curMins) => {
  const bySuffix = {};
  (universe || []).forEach(t => {
    const s = getSuffix(t.ticker);
    (bySuffix[s] = bySuffix[s] || []).push(t);
  });

  const norm = (currency) => (t) => ({
    ...t, ticker: baseSymbol(t.ticker), fullTicker: t.ticker,
    price: t.ult, change: t.var, currency: currency || t.currency,
  });

  return EXCHANGES.map(exch => {
    const isOpen = isExchangeOpen(exch, curMins);
    let gainers = [], losers = [], movers = [], anchors = [];

    if (exch.id === 'B3') {
      const b3 = norm('R$');
      gainers = b3Gainers.slice(0, 6).map(b3);
      losers  = b3Losers.slice(0, 6).map(b3);
      movers  = b3Active.slice(0, 9).map(b3);
      anchors = movers; // B3 bellwethers = most-traded (volume)
    } else {
      const pool = (exch.suffixes || []).flatMap(s => bySuffix[s] || []);
      if (!pool.length) return null;
      const m = norm();
      const sorted = [...pool].sort((a, b) => (b.var ?? -Infinity) - (a.var ?? -Infinity));
      gainers = sorted.filter(t => (t.var ?? 0) >= 0).slice(0, 6).map(m);
      losers  = sorted.filter(t => (t.var ?? 0) < 0).slice(-6).reverse().map(m);
      // no volume off B3 → rank the heatmap/movers by absolute move
      movers  = [...pool].sort((a, b) => Math.abs(b.var ?? 0) - Math.abs(a.var ?? 0)).slice(0, 9).map(m);
      // trio anchors = curated bellwethers present in the universe, then fill with movers
      const want = ANCHORS[exch.id] || [];
      const rawByFull = new Map(pool.map(t => [t.ticker, t]));
      const anchorItems = want.map(tk => rawByFull.get(tk)).filter(Boolean).map(m);
      const seen = new Set(anchorItems.map(x => x.fullTicker));
      anchors = [...anchorItems, ...movers.filter(x => !seen.has(x.fullTicker))].slice(0, 9);
    }

    // ticker tape items — combined
    const tape = [...gainers, ...losers].filter((t,i,a) => a.findIndex(x=>x.ticker===t.ticker)===i);

    return { ...exch, isOpen, gainers, losers, movers, anchors, tape };
  }).filter(Boolean).filter(e => e.tape.length > 0 || e.id === 'B3');
};

// ════════════════════════════════════════════════════
// useExchangeRotation — state machine (freezable)
// Phases: OPEN (30s) → GLOBE_TOUR (4–8s per exchange) → COMMERCIAL (6s) → OPEN
// `frozen` halts every timer (attract-mode default, any user click freezes).
// `pinnedId` forces the displayed exchange when the user pins one.
// ════════════════════════════════════════════════════
export const PHASE_OPEN       = 'OPEN';
export const PHASE_TOUR       = 'TOUR';
export const PHASE_COMMERCIAL = 'COMMERCIAL';

export function useExchangeRotation(exchMap, frozen, pinnedId) {
  const [phase,      setPhase]      = useState(PHASE_OPEN);
  const [tourIdx,    setTourIdx]    = useState(0);
  const [fading,     setFading]     = useState(false);
  const timer = useRef(null);

  const openExchanges   = exchMap.filter(e => e.isOpen);
  const closedExchanges = exchMap.filter(e => !e.isOpen);
  const tourList        = [...closedExchanges, ...openExchanges.slice(1)]; // skip primary open during tour

  const primaryOpen = openExchanges[0] || exchMap[0];

  const advance = useCallback((nextFn) => {
    setFading(true);
    setTimeout(() => { nextFn(); setFading(false); }, 350);
  }, []);

  useEffect(() => {
    if (!exchMap.length) return;
    clearTimeout(timer.current);

    if (frozen) { setFading(false); return; } // user froze the feed — no auto-cycle

    if (phase === PHASE_OPEN) {
      timer.current = setTimeout(() => {
        if (tourList.length === 0) {
          // no tour, go straight to commercial
          advance(() => setPhase(PHASE_COMMERCIAL));
        } else {
          advance(() => { setTourIdx(0); setPhase(PHASE_TOUR); });
        }
      }, 30000);

    } else if (phase === PHASE_TOUR) {
      const seg = tourList[tourIdx];
      const dwell = seg?.isOpen ? 8000 : 4000;
      timer.current = setTimeout(() => {
        const next = tourIdx + 1;
        if (next >= tourList.length) {
          // insert commercial after tour
          advance(() => setPhase(PHASE_COMMERCIAL));
        } else {
          advance(() => setTourIdx(next));
        }
      }, dwell);

    } else if (phase === PHASE_COMMERCIAL) {
      timer.current = setTimeout(() => {
        advance(() => setPhase(PHASE_OPEN));
      }, 6000);
    }

    return () => clearTimeout(timer.current);
  }, [phase, tourIdx, exchMap.length, frozen]);

  // Derive current display exchange
  let current = primaryOpen;
  if (pinnedId) {
    current = exchMap.find(e => e.id === pinnedId) || primaryOpen;
  } else if (phase === PHASE_TOUR && tourList[tourIdx]) {
    current = tourList[tourIdx];
  }
  const effectivePhase = pinnedId ? PHASE_OPEN : phase;

  return { phase: effectivePhase, current, fading, primaryOpen };
}
